import type {
  GoalResultVariant,
  OnboardingWizardStep,
  QuizAnswers,
} from "@components/OnboardingWizard/types";
import { WIZARD_STEP_ORDER } from "@components/OnboardingWizard/types";
import type { OnboardingV3ResultValues } from "./onboardingV3Types";

type HarnessAuthMode = "guest" | "onboarding" | "completed";
type HarnessLoginPhase = "intro" | "login";
export type HarnessQuizState = "empty" | "filled";
type HarnessEmailStep = "email" | "code";

const WIZARD_STEPS_WITH_ERROR: OnboardingWizardStep[] = [
  ...WIZARD_STEP_ORDER,
  "loading-error",
];

const RESULT_VARIANTS: GoalResultVariant[] = [
  "million",
  "home",
  "runway",
  "idle-cash",
  "retirement",
  "debt-free",
];

export interface OnboardingHarnessQuery {
  /** Harness query 是否生效（dev / NEXT_PUBLIC_HARNESS=1） */
  enabled: boolean;
  auth: HarnessAuthMode | null;
  loginPhase: HarnessLoginPhase | null;
  /** Intro 轮播定格 0–3 */
  introSlide: number | null;
  wizardStep: OnboardingWizardStep | null;
  resultVariant: GoalResultVariant | null;
  simulateLoadingError: boolean;
  quizState: HarnessQuizState | null;
  openPlanModal: boolean;
  openPaywall: boolean;
  /** Playwright harness: keep LoaderStep mounted (skip auto-advance to result) */
  freezeLoader: boolean;
  emailStep: HarnessEmailStep | null;
  codeError: boolean;
}

export function isOnboardingHarnessEnabled(): boolean {
  return (
    process.env.NODE_ENV !== "production" ||
    process.env.NEXT_PUBLIC_HARNESS === "1"
  );
}

function parseAuth(value: string | null): HarnessAuthMode | null {
  if (value === "guest" || value === "onboarding" || value === "completed") {
    return value;
  }
  return null;
}

function parseLoginPhase(value: string | null): HarnessLoginPhase | null {
  if (value === "intro" || value === "login") {
    return value;
  }
  return null;
}

function parseWizardStep(value: string | null): OnboardingWizardStep | null {
  if (!value) {
    return null;
  }
  return WIZARD_STEPS_WITH_ERROR.includes(value as OnboardingWizardStep)
    ? (value as OnboardingWizardStep)
    : null;
}

function parseResultVariant(value: string | null): GoalResultVariant | null {
  if (!value) {
    return null;
  }
  return RESULT_VARIANTS.includes(value as GoalResultVariant)
    ? (value as GoalResultVariant)
    : null;
}

function parseIntroSlide(value: string | null): number | null {
  if (value === null || value === "") {
    return null;
  }
  const index = Number.parseInt(value, 10);
  if (!Number.isFinite(index) || index < 0 || index > 3) {
    return null;
  }
  return index;
}

function parseQuizState(value: string | null): HarnessQuizState | null {
  if (value === "empty" || value === "filled") {
    return value;
  }
  return null;
}

function parseEmailStep(value: string | null): HarnessEmailStep | null {
  if (value === "email" || value === "code") {
    return value;
  }
  return null;
}

function parseTruthyFlag(value: string | null): boolean {
  return value === "1" || value === "true";
}

/** Harness `harnessQuizState=filled` 时预填的答案（便于空/满态对照） */
export function buildHarnessQuizAnswers(
  quizState: HarnessQuizState | null,
  prefilledName = "",
): Partial<QuizAnswers> {
  if (quizState === "empty") {
    return {
      name: "",
      age: "",
      income: 0,
      expenses: 0,
      cash: 0,
      debt: 0,
      invest: 0,
      risk: "",
      goalId: "",
    };
  }

  if (quizState !== "filled") {
    return {};
  }

  return {
    name: prefilledName || "Alex",
    age: "25-34",
    income: 85_000,
    expenses: 3_200,
    cash: 24_000,
    debt: 24_000,
    invest: 40_000,
    risk: "balanced",
    goalId: "million",
  };
}

/** Harness result step fixture — mirrors RESULT_VARIANT_CONFIG + chart payloads. */
export function buildHarnessResultValues(
  variant: GoalResultVariant,
): OnboardingV3ResultValues | null {
  switch (variant) {
    case "million":
      return {
        goalType: "million",
        values: {
          onYourOwnYear: 2043,
          withEdYear: 2038,
          onYourOwnAge: 57,
          targetAmount: 1_000_000,
          deltaYears: 5,
          chart: {
            onYourOwn: [4, 19, 36, 57, 81, 100],
            withEd: [4, 22, 45, 72, 100],
          },
        },
      };
    case "home":
      return {
        goalType: "home",
        values: {
          onYourOwnHomePrice: 420_000,
          withEdHomePrice: 500_000,
          deltaHomePrice: 80_000,
          yearsToTarget: 3,
          chart: {
            savedDownPayment: 84_000,
            targetDownPayment: 90_000,
            withEdTargetDownPayment: 98_000,
          },
        },
      };
    case "runway":
      return {
        goalType: "emergency",
        values: {
          runwayMonths: 8,
          safeMonths: 6,
          deltaMonths: 4,
          chart: { current: 8, safeTarget: 6, withEd: 12, deltaMonths: 4 },
        },
      };
    case "idle-cash":
      return {
        goalType: "idlecash",
        values: {
          idleCashAmount: 31_000,
          annualExtraYield: 1_600,
          longTermGain: 34_000,
          longTermYears: 15,
          chart: { currentYield: 0.5, withEdYield: 4.5 },
        },
      };
    case "retirement":
      return {
        goalType: "retirement",
        values: {
          onYourOwnAge: 62,
          onYourOwnYear: 2043,
          withEdYear: 2040,
          fiAmount: 1_200_000,
          deltaYears: 3,
          chart: {
            onYourOwn: [4, 19, 36, 57, 81, 100],
            withEd: [4, 22, 45, 72, 100],
          },
        },
      };
    case "debt-free":
      return {
        goalType: "debt",
        values: {
          onYourOwnYear: 2031,
          withEdYear: 2029,
          debtAmount: 24_000,
          deltaMonths: 24,
          chart: { onYourOwnYears: 6, withEdYears: 4 },
        },
      };
    default:
      return null;
  }
}

/**
 * 从 URLSearchParams 解析 V3 onboarding harness fixture（纯函数，可单测）。
 */
export function parseOnboardingHarnessQuery(
  searchParams: Pick<URLSearchParams, "get">,
  options: { harnessEnabled?: boolean } = {},
): OnboardingHarnessQuery {
  const enabled = options.harnessEnabled ?? isOnboardingHarnessEnabled();

  if (!enabled) {
    return {
      enabled: false,
      auth: null,
      loginPhase: null,
      introSlide: null,
      wizardStep: null,
      resultVariant: null,
      simulateLoadingError: false,
      quizState: null,
      openPlanModal: false,
      openPaywall: false,
      freezeLoader: false,
      emailStep: null,
      codeError: false,
    };
  }

  const simulateLoadingError = parseTruthyFlag(
    searchParams.get("harnessSimulateLoadingError"),
  );
  const wizardStepRaw = searchParams.get("harnessWizardStep");
  let wizardStep = parseWizardStep(wizardStepRaw);

  if (
    wizardStep === "loading" &&
    simulateLoadingError &&
    wizardStepRaw === "loading"
  ) {
    wizardStep = "loading-error";
  }

  return {
    enabled: true,
    auth: parseAuth(searchParams.get("harnessAuth")),
    loginPhase: parseLoginPhase(searchParams.get("harnessLoginPhase")),
    introSlide: parseIntroSlide(searchParams.get("harnessIntroSlide")),
    wizardStep,
    resultVariant: parseResultVariant(searchParams.get("harnessResultVariant")),
    simulateLoadingError,
    quizState: parseQuizState(searchParams.get("harnessQuizState")),
    openPlanModal: parseTruthyFlag(searchParams.get("harnessOpenPlanModal")),
    openPaywall: parseTruthyFlag(searchParams.get("harnessOpenPaywall")),
    freezeLoader: parseTruthyFlag(searchParams.get("harnessFreezeLoader")),
    emailStep: parseEmailStep(searchParams.get("harnessEmailStep")),
    codeError: parseTruthyFlag(searchParams.get("harnessCodeError")),
  };
}
